import React, { useEffect, useMemo, useState } from 'react'
import { adminRequest } from '../api/client'
import { Card, EmptyState, PageTitle, SearchField, StatusBadge, formatDate } from '../components/Ui'
import { BackLink, DetailGrid, SummaryGrid } from '../components/AdminCommon'
import AdminFormModal, { FormField, FormGrid } from '../components/AdminFormModal'

function shortId(id) {
  return id ? `INQ-${String(id).slice(0, 8).toUpperCase()}` : '-'
}

function requester(item) {
  if (item.requesterRole === 'MEMBER') return item.memberId || item.requesterAccountId
  if (item.requesterRole === 'DIRECTOR') return item.directorId || item.requesterAccountId
  return item.requesterAccountId || '-'
}

export function Inquiries({ navigate }) {
  const [items, setItems] = useState([])
  const [search, setSearch] = useState('')
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')

  useEffect(() => {
    let disposed = false
    adminRequest('/api/v1/admin/support-inquiries')
      .then((response) => { if (!disposed) setItems(response?.data || []) })
      .catch((nextError) => { if (!disposed) setError(nextError?.message || '문의를 불러오지 못했습니다.') })
      .finally(() => { if (!disposed) setLoading(false) })
    return () => { disposed = true }
  }, [])

  const rows = useMemo(() => items.filter((item) => !search || [item.id,item.requesterRole,requester(item),item.subject,item.category,item.content].join(' ').toLowerCase().includes(search.toLowerCase())), [items, search])
  return <>
    <PageTitle eyebrow="CUSTOMER SUPPORT · LIVE DB" title="문의 관리" description="회원·디렉터가 실제 등록한 support_inquiries 원장을 관리합니다." />
    <div className="policy-callout"><strong>실제 문의 원장</strong><span>마이페이지·디렉터 센터의 문의와 동일한 ID·내용·PRIVATE 첨부파일을 조회합니다. 예시 문의를 별도 생성하지 않습니다.</span></div>
    <Card className="table-card"><div className="table-toolbar"><SearchField value={search} onChange={setSearch} placeholder="문의 ID, 요청자, 제목, 유형 검색" /></div><div className="table-result-bar"><span>문의 <strong>{rows.length}</strong>건</span><small>source: support_inquiries</small></div>{loading ? <p className="help-copy">실제 문의를 불러오는 중입니다.</p> : error ? <EmptyState title="문의 조회 실패" description={error} /> : rows.length ? <div className="table-scroll"><table className="data-table"><thead><tr><th>문의번호</th><th>요청자 유형</th><th>요청자 ID</th><th>유형</th><th>제목</th><th>첨부</th><th>상태</th><th>접수일</th></tr></thead><tbody>{rows.map((item) => <tr className="clickable-row" key={item.id} onClick={() => navigate(`/support/inquiries/${item.id}`)}><td><strong>{shortId(item.id)}</strong><small className="mono">{item.id}</small></td><td>{item.requesterRole}</td><td><small className="mono">{requester(item)}</small></td><td>{item.category}</td><td><strong>{item.subject}</strong></td><td>{(item.attachmentMediaIds || []).length}개</td><td><StatusBadge status={item.status} /></td><td>{formatDate(item.createdAt, true)}</td></tr>)}</tbody></table></div> : <EmptyState title="접수된 문의가 없습니다." description="회원 또는 디렉터가 문의를 등록하면 실제 데이터가 표시됩니다." />}</Card>
  </>
}

export function InquiryDetail({ id, navigate }) {
  const [item, setItem] = useState(null)
  const [loading, setLoading] = useState(true)
  const [error, setError] = useState('')
  const [answerOpen, setAnswerOpen] = useState(false)
  const [answer, setAnswer] = useState('')
  const [notice, setNotice] = useState('')

  const load = async () => {
    setLoading(true)
    setError('')
    try {
      const response = await adminRequest(`/api/v1/admin/support-inquiries/${encodeURIComponent(id)}`)
      setItem(response?.data || null)
    } catch (nextError) {
      setError(nextError?.message || '문의를 찾을 수 없습니다.')
    } finally {
      setLoading(false)
    }
  }
  useEffect(() => { load() }, [id])

  const submitAnswer = async (event) => {
    event.preventDefault()
    if (!answer.trim()) return
    setNotice('')
    try {
      await adminRequest(`/api/v1/admin/support-inquiries/${encodeURIComponent(id)}/answer`, { method:'POST', body:{answer:answer.trim()} })
      setAnswerOpen(false)
      setAnswer('')
      setNotice('답변을 실제 support_inquiries 원장에 저장했습니다.')
      await load()
    } catch (nextError) {
      setNotice(nextError?.message || '답변 저장에 실패했습니다.')
    }
  }

  if (loading) return <><BackLink onClick={() => navigate('/support/inquiries')} /><Card><p>실제 문의를 조회하는 중입니다.</p></Card></>
  if (error || !item) return <><BackLink onClick={() => navigate('/support/inquiries')} /><Card><EmptyState title="문의 조회 실패" description={error || '문의를 찾을 수 없습니다.'} /></Card></>

  const attachments = item.attachmentMediaIds || []
  return <>
    <BackLink onClick={() => navigate('/support/inquiries')} />
    <PageTitle eyebrow="INQUIRY DETAIL · LIVE DB" title={item.subject} description={`${shortId(item.id)} · ${item.requesterRole}`} actions={item.status !== 'CLOSED' ? <button className="button primary" onClick={() => { setAnswer(item.answer || ''); setAnswerOpen(true) }}>답변 등록·수정</button> : null} />
    {notice && <div className="admin-notice"><strong>안내</strong><span>{notice}</span></div>}
    <SummaryGrid items={[
      { label:'상태', value:<StatusBadge status={item.status} /> },
      { label:'요청자 유형', value:item.requesterRole },
      { label:'문의 유형', value:item.category },
      { label:'첨부 파일', value:`${attachments.length}개` },
    ]} />
    <div className="dashboard-grid half">
      <Card title="문의 등록 내용"><DetailGrid items={[
        { label:'제목', value:item.subject || '-', wide:true },
        { label:'문의 내용', value:item.content || '-', wide:true },
        { label:'접수일', value:formatDate(item.createdAt, true) },
        { label:'요청자 계정 ID', value:item.requesterAccountId || '-', wide:true },
        { label:'회원 ID', value:item.memberId || '-' },
        { label:'디렉터 ID', value:item.directorId || '-' },
      ]} />{attachments.length ? <div className="admin-file-list">{attachments.map((mediaId, index) => <a key={mediaId} href={`/api/v1/admin/media/${mediaId}/content`} target="_blank" rel="noreferrer"><span>▧</span><strong>첨부파일 {index + 1}</strong><small>{mediaId}</small></a>)}</div> : <div className="admin-empty-inline">첨부 파일이 없습니다.</div>}</Card>
      <Card title="답변"><div className="admin-long-text">{item.answer || '아직 등록된 답변이 없습니다.'}</div>{item.answeredAt && <p className="admin-answer-meta">답변일 {formatDate(item.answeredAt, true)}</p>}{item.closedAt && <p className="admin-answer-meta">종료일 {formatDate(item.closedAt, true)}</p>}</Card>
    </div>
    <AdminFormModal open={answerOpen} title="문의 답변 등록" description="저장하면 실제 문의 원장 상태가 ANSWERED로 변경되고 관리자 감사로그가 남습니다." onClose={() => setAnswerOpen(false)} onSubmit={submitAnswer} submitLabel="답변 저장"><FormGrid><FormField label="답변" wide required><textarea required value={answer} onChange={(event) => setAnswer(event.target.value)} /></FormField></FormGrid></AdminFormModal>
  </>
}

export function Conversations() {
  return <><PageTitle eyebrow="CONVERSATIONS" title="대화·분쟁 조회" description="실제 관리자 대화 조회 API가 연결되기 전까지 가짜 대화 내용을 표시하지 않습니다." /><Card><EmptyState title="관리자 대화 조회 원장 미연결" description="회원·디렉터 메시지 원장은 존재하지만 관리자 조회 권한·감사 계약을 먼저 확정한 뒤 연결합니다." /></Card></>
}

export function ConversationDetail() {
  return <Card><EmptyState title="관리자 대화 조회 원장 미연결" /></Card>
}

export function Notifications({ navigate }) {
  return <><PageTitle eyebrow="NOTIFICATIONS" title="알림 발송" description="알림 규칙 관리 화면을 사용합니다." /><Card><button className="button primary" onClick={() => navigate?.('/support/notifications')}>실제 알림 규칙으로 이동</button></Card></>
}

export function NotificationDetail({ navigate }) {
  return <><BackLink onClick={() => navigate?.('/support/notifications')} /><Card><EmptyState title="알림 규칙 화면에서 실제 발송 설정을 확인해주세요." /></Card></>
}
